import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { useCurrencyUtils } from "../../utils/currencyUtils";
import api from "../../lib/api.ts";

export default function SellerPlansSection() {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";

  // Properly destructure both currency functions


  const [sellerPackages, setSellerPackages] = useState([]);
  const [packageFilter, setPackageFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSellerPackages = async () => {
      try {
        setLoading(true);
        const response = await api.get("/sellerpackages");
        console.log("Fetched seller packages:", response.data);
        setSellerPackages(response.data || []);
      } catch (err) {
        console.error("Error fetching seller packages:", err);
        setError(
          translate("sellerPage.error.loadingPackages") ||
          "Failed to load packages"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchSellerPackages();
  }, [translate]);

  const filteredPackages = sellerPackages.filter((pkg) =>
    packageFilter === "All" ? true : pkg.sellerType?.name === packageFilter
  );

  return (
    <section
      className="bg-bg-dark py-16 px-4 font-sans"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {translate("sellerPage.choosePlan")
              .split(" ")
              .map((word, i) => (
                <span
                  key={i}
                  className={
                    word.toLowerCase() === "choose" || word.toLowerCase() === "اختر"
                      ? "text-primary"
                      : ""
                  }
                >
                  {word}{" "}
                </span>
              ))}
          </h2>
          <p className="text-primary-soft/70 text-lg max-w-2xl mx-auto">
            {translate("sellerPage.choosePlanSubtitle")}
          </p>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          className="flex justify-center gap-3 flex-wrap mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {["All", "Individual", "Corporate"].map((type) => (
            <FilterButton
              key={type}
              label={translate(`sellerPage.filters.${type.toLowerCase()}`)}
              active={packageFilter === type}
              onClick={() => setPackageFilter(type)}
            />
          ))}
        </motion.div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : error ? (
          <div className="text-center py-16 text-red-400">{error}</div>
        ) : filteredPackages.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            {translate("sellerPage.noPackagesFound") ||
              "No packages found for selected filter"}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPackages.map((pkg, index) => (
              <SellerPackageCard
                key={pkg.id}
                pkg={pkg}
                index={index}
                isRTL={isRTL}
                translate={translate}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// Filter Button Component (unchanged)
function FilterButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-8 py-2.5 rounded-lg font-medium transition-all duration-300 border ${active
        ? "bg-primary border-primary text-white shadow-lg shadow-primary/20"
        : "bg-bg-medium border-bg-light text-primary-soft hover:border-primary/50 hover:text-primary"
        }`}
    >
      {label}
    </button>
  );
}

// Seller Package Card Component – now with currency conversion
function SellerPackageCard({ pkg, index, isRTL, translate }) {
  const isCorporate = pkg.sellerType?.name === "Corporate";
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();

  return (
    <motion.div
      className="bg-bg-medium border border-bg-light rounded-2xl overflow-hidden flex flex-col h-full group hover:border-primary/40 transition-colors"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      {/* Header */}
      <div className="p-8 border-b border-bg-light bg-gradient-to-br from-bg-blue/20 to-transparent">
        <div className="flex justify-between items-center mb-3">
          <span className="text-primary-light text-xs font-bold uppercase tracking-widest">
            {pkg.sellerType?.name || "Standard"}
          </span>
          {isCorporate && (
            <span className="bg-primary/10 text-primary text-[10px] px-2.5 py-1 rounded border border-primary/20 font-medium">
              POPULAR
            </span>
          )}
        </div>
        <h3 className="text-2xl font-bold text-white group-hover:text-primary transition-colors">
          {pkg.name}
        </h3>
      </div>

      {/* Location */}
      <div
        className={`flex items-center gap-2 px-6 text-primary-soft/70 text-sm ${isRTL ? "flex-row-reverse" : ""
          }`}
      >
        <MapPin className="w-4 h-4 text-primary shrink-0" />
        <span>
          {pkg.location?.name ||
            translate("sellerPage.location") ||
            "Global / Online"}
        </span>
      </div>

      {/* Features */}
      <div className="px-8 py-6 flex-grow">
        <h4
          className={`text-sm font-bold text-primary-soft uppercase tracking-wider mb-5 ${isRTL ? "text-right" : "text-left"
            }`}
        >
          {translate("sellerPage.features.heading")}
        </h4>
        <ul className="space-y-4">
          {[
            { key: "listingFee", label: "sellerPage.features.listingFee" },
            { key: "numberOfRuns", label: "sellerPage.features.numberOfRuns" },
            { key: "freeParkingDays", label: "sellerPage.features.freeParkingDays" },
          ].map((item, i) => (
            <li
              key={i}
              className={`flex items-center gap-3 text-sm text-gray-200 ${isRTL ? "flex-row-reverse text-right" : ""
                }`}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
              <span className="flex-1">
                {translate(item.label)}:{" "}
                {item.key === "listingFee" ? (
                  <span className="text-white font-semibold">
                    {displayPriceWithSymbol(convertPrice(Number(pkg.listingFee || 0)))}
                  </span>
                ) : (
                  <span className="text-white font-semibold">
                    {pkg[item.key] || "0"}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Pricing Table */}
      <div className="px-8 pb-6">
        <div className="rounded-xl border border-bg-light/40 overflow-hidden bg-bg-darker/40">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-bg-light/30 text-primary-soft/70 uppercase tracking-wider">
                <th className="p-3.5 text-left font-semibold">
                  {translate("sellerPage.pricingTable.fee")}
                </th>
                <th className="p-3.5 text-right font-semibold">Range</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bg-light/30 text-gray-300">
              {pkg.sellerPackageRanges?.map((range, i) => (
                <tr key={i}>
                  <td className="p-3.5 font-medium text-primary-light">
                    {displayPriceWithSymbol(convertPrice(Number(range.fee || 0)))}
                  </td>
                  <td className="p-3.5 text-right">
                    {displayPriceWithSymbol(convertPrice(Number(range.startRange || 0)))} – {displayPriceWithSymbol(convertPrice(Number(range.toRange || 0)))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action */}
      <div className="p-8 mt-auto border-t border-bg-light/20">
        <button className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg shadow-primary/20 disabled:opacity-60 disabled:cursor-not-allowed">
          {translate("sellerPage.buyNow")}
        </button>
      </div>
    </motion.div>
  );
}